import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import merge from 'lodash/merge';
import compact from 'lodash/compact';

import {
  selectBloodGlucoseMeasurementEntity,
  selectBloodGlucoseMeasurementModalHistoricList,
  selectHeartRateMeasurementEntity,
  selectHeartRateMeasurementModalHistoricList,
  selectSaturationMeasurementEntity,
  selectSaturationMeasurementModalHistoricList,
} from '../../../redux/profiles/patientHealthboard/selectors';
import {
  makeValueForBothDataset,
  makeSplitDataForSaturation,
  makeValueForGraf,
  checkForAvailableSourceData,
  getSourceDataWithType,
  getCategoryPercentageWithFilter,
  createTooltips,
  adaptResponse,
  getConvertedSafeData,
} from '../utils';
import { DATA_SOURCE_TYPE, VITALS_ACTIVITY_TYPE } from '../constants';
import { useSafeDataSourceTitle } from '../../common/Charts/hooks';

const { SATURATION, HEART_RATE, BLOOD_GLUCOSE } = VITALS_ACTIVITY_TYPE;
const { CCD, DEVICES, ALL_SOURCES } = DATA_SOURCE_TYPE;

export function useSaturationDataGraf({
  rowData,
  activeFilter,
  singleDayMode,
  options,
  isCandleType,
  sourceType,
  currentChartInstance,
  formatMessage,
  mainUser,
  targetUserFirstName,
  type,
  isMilligramsPerDeciliter,
}) {
  const isWithoutLimit = [HEART_RATE, BLOOD_GLUCOSE].includes(type);

  const saturationDataEntity = adaptResponse(
    useSelector(selectSaturationMeasurementEntity),
  );
  const saturationDataModal = adaptResponse(
    useSelector(selectSaturationMeasurementModalHistoricList),
  );
  const heartRateDataEntity = adaptResponse(
    useSelector(selectHeartRateMeasurementEntity),
  );
  const heartRateDataModal = adaptResponse(
    useSelector(selectHeartRateMeasurementModalHistoricList),
  );
  const bloodGlucoseDataEntity = adaptResponse(
    useSelector(selectBloodGlucoseMeasurementEntity),
    { type, isMilligramsPerDeciliter },
  );
  const bloodGlucoseDataModal = adaptResponse(
    useSelector(selectBloodGlucoseMeasurementModalHistoricList),
    { type, isMilligramsPerDeciliter },
  );

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const { safeDataEntity, safeDataModal } = useMemo(() => {
    switch (type) {
      case HEART_RATE:
        return {
          safeDataEntity: heartRateDataEntity,
          safeDataModal: heartRateDataModal,
        };
      case SATURATION:
        return {
          safeDataEntity: saturationDataEntity,
          safeDataModal: saturationDataModal,
        };
      case BLOOD_GLUCOSE:
        return {
          safeDataEntity: bloodGlucoseDataEntity,
          safeDataModal: bloodGlucoseDataModal,
        };
      default:
        return {
          safeDataEntity: [],
          safeDataModal: [],
        };
    }
  }, [
    type,
    heartRateDataEntity,
    saturationDataEntity,
    bloodGlucoseDataEntity,
    heartRateDataModal,
    saturationDataModal,
    bloodGlucoseDataModal,
  ]);

  const ccdSourceTooltipLabel = getSafeDataSourceTitle(
    CCD,
    formatMessage,
    mainUser,
    targetUserFirstName,
  );

  const deviceSourceTooltipLabel = getSafeDataSourceTitle(
    DEVICES,
    formatMessage,
    mainUser,
    targetUserFirstName,
  );

  const saturationDataModalDevices = getSourceDataWithType(
    safeDataModal,
    DEVICES,
  );

  const saturationDataModalCCD = getSourceDataWithType(safeDataModal, CCD);

  const saturationDataDevices = getSourceDataWithType(safeDataEntity, DEVICES);

  const saturationDataCCD = getSourceDataWithType(safeDataEntity, CCD);

  const safeSaturationDataDevices = singleDayMode
    ? saturationDataModalDevices
    : saturationDataDevices;
  const safeSaturationDataCCD = singleDayMode
    ? saturationDataModalCCD
    : saturationDataCCD;

  const timeSetsCCD = checkForAvailableSourceData(safeSaturationDataCCD)
    ? makeValueForGraf(
        safeSaturationDataCCD.measurements,
        'value',
        activeFilter,
        true,
        SATURATION,
        singleDayMode,
      )
    : [];

  const timeSetsDevices = checkForAvailableSourceData(safeSaturationDataDevices)
    ? makeValueForGraf(
        safeSaturationDataDevices.measurements,
        'value',
        activeFilter,
        true,
        SATURATION,
        singleDayMode,
      )
    : [];

  useEffect(() => {
    if (currentChartInstance && currentChartInstance.chartInstance) {
      currentChartInstance.chartInstance.update();
    }
  }, [currentChartInstance, sourceType]);

  const {
    transparentDataEntity: transparentDataEntityDevices,
    valueDataEntity: valueDataEntityDevices,
  } = useMemo(
    () =>
      makeSplitDataForSaturation(
        safeSaturationDataDevices,
        activeFilter,
        isCandleType,
        isWithoutLimit,
      ),
    [
      safeDataEntity,
      singleDayMode,
      safeDataModal,
      saturationDataModalDevices,
      activeFilter,
      isCandleType,
      safeSaturationDataDevices,
      isWithoutLimit,
    ],
  );

  const {
    transparentDataEntity: transparentDataEntityCCD,
    valueDataEntity: valueDataEntityDevicesCCD,
  } = useMemo(
    () =>
      makeSplitDataForSaturation(
        safeSaturationDataCCD,
        activeFilter,
        isCandleType,
        isWithoutLimit,
      ),
    [
      safeDataEntity,
      singleDayMode,
      safeDataModal,
      saturationDataModalCCD,
      safeSaturationDataCCD,
      activeFilter,
      isCandleType,
      isWithoutLimit,
    ],
  );

  if (!isCandleType) return {};

  const {
    valueData: valueDataDevices,
    transparentData: transparentDataDevices,
    ranges: rangesDevices,
    maxValueForRange: maxValueForRangeDevices,
    minValueForRange: minValueForRangeDevices,
    minLimitForYAxis: minLimitForYAxisDevices,
    maxLimitForYAxis: maxLimitForYAxisDevices,
  } = makeValueForBothDataset({
    baseTimeArr: rowData.timeSets,
    transparentEntity: transparentDataEntityDevices,
    valueEntity: valueDataEntityDevices,
    activeFilter,
    dataType: type,
    isWithoutLimit,
    isMilligramsPerDeciliter,
  });

  const {
    valueData: valueDataCCD,
    transparentData: transparentDataCCD,
    ranges: rangesCCD,
    maxValueForRange: maxValueForRangeCCD,
    minValueForRange: minValueForRangeCCD,
    minLimitForYAxis: minLimitForYAxisCCD,
    maxLimitForYAxis: maxLimitForYAxisCCD,
  } = makeValueForBothDataset({
    baseTimeArr: rowData.timeSets,
    transparentEntity: transparentDataEntityCCD,
    valueEntity: valueDataEntityDevicesCCD,
    activeFilter,
    dataType: type,
    isWithoutLimit,
    isMilligramsPerDeciliter,
  });

  const {
    concatMinValueForRange,
    concatMaxValueForRange,
    concatMinLimitForYAxis,
    concatMaxLimitForYAxis,
  } = (() => {
    switch (sourceType) {
      case ALL_SOURCES:
        return {
          concatMinValueForRange: Math.min(
            ...compact([minValueForRangeCCD, minValueForRangeDevices]),
          ),
          concatMaxValueForRange: Math.max(
            ...compact([maxValueForRangeCCD, maxValueForRangeDevices]),
          ),
          concatMinLimitForYAxis: Math.min(
            ...compact([minLimitForYAxisCCD, minLimitForYAxisDevices]),
          ),
          concatMaxLimitForYAxis: Math.max(
            ...compact([maxLimitForYAxisCCD, maxLimitForYAxisDevices]),
          ),
        };
      case DEVICES:
        return {
          concatMinValueForRange: minValueForRangeDevices,
          concatMaxValueForRange: maxValueForRangeDevices,
          concatMinLimitForYAxis: minLimitForYAxisDevices,
          concatMaxLimitForYAxis: maxLimitForYAxisDevices,
        };
      case CCD:
        return {
          concatMinValueForRange: minValueForRangeCCD,
          concatMaxValueForRange: maxValueForRangeCCD,
          concatMinLimitForYAxis: minLimitForYAxisCCD,
          concatMaxLimitForYAxis: maxLimitForYAxisCCD,
        };
    }
  })();

  const safeRangeTitle =
    concatMinValueForRange === concatMaxValueForRange
      ? concatMinValueForRange
      : `${
          Number.isFinite(concatMinValueForRange)
            ? getConvertedSafeData(concatMinValueForRange, {
                type,
                isMilligramsPerDeciliter,
                forRound: true,
              })
            : ''
        } - ${
          Number.isFinite(concatMaxValueForRange)
            ? getConvertedSafeData(concatMaxValueForRange, {
                type,
                isMilligramsPerDeciliter,
                forRound: true,
              })
            : ''
        }`;

  const safeDataEntityDevices =
    safeDataEntity && safeDataEntity.length ? safeDataEntity[0] : [];
  const safeDataModalDevices =
    safeDataModal && safeDataModal.length ? safeDataModal[0] : [];

  const safeData = singleDayMode ? safeDataModalDevices : safeDataEntityDevices;
  const datasets = [
    {
      label: 'transparentDataDevices',
      data: [DEVICES, ALL_SOURCES].includes(sourceType)
        ? transparentDataDevices
        : [],
      backgroundColor: 'transparent',
      pointHitRadius: 0,
      hoverBorderWidth: 0,
      barThickness: 'flex',
      categoryPercentage: getCategoryPercentageWithFilter(
        activeFilter,
        singleDayMode,
        rangesDevices.length === 1,
        ALL_SOURCES !== sourceType,
      ),
      barPercentage: 0.92,
      ranges: rangesDevices,
      stack: 1,
      sourceType: {
        title: deviceSourceTooltipLabel,
        code: DATA_SOURCE_TYPE.DEVICES,
        codes: safeData.measurements
          ? safeData.measurements.map(el => {
              return {
                providerCode: el.originalProviderCodes,
                value: el.value,
                measuredAt: el.measuredAt,
              };
            })
          : null,
      },
    },
    {
      label: 'valueDataDevices',
      data: [DEVICES, ALL_SOURCES].includes(sourceType) ? valueDataDevices : [],
      backgroundColor: '#326cba',
      hoverBackgroundColor: '#326cba',
      hoverBorderWidth: 1,
      barThickness: 'flex',
      categoryPercentage: getCategoryPercentageWithFilter(
        activeFilter,
        singleDayMode,
        rangesDevices.length === 1,
        ALL_SOURCES !== sourceType,
      ),
      barPercentage: 0.92,
      ranges: rangesDevices,
      stack: 1,
      sourceType: {
        title: deviceSourceTooltipLabel,
        code: DATA_SOURCE_TYPE.DEVICES,
        codes: safeData.measurements
          ? safeData.measurements.map(el => {
              return {
                providerCode: el.originalProviderCodes,
                value: el.value,
                measuredAt: el.measuredAt,
              };
            })
          : null,
      },
    },
    {
      label: 'transparentDataCCD',
      data: [CCD, ALL_SOURCES].includes(sourceType) ? transparentDataCCD : [],
      backgroundColor: 'transparent',
      pointHitRadius: 0,
      hoverBorderWidth: 0,
      barThickness: 'flex',
      categoryPercentage: getCategoryPercentageWithFilter(
        activeFilter,
        singleDayMode,
        rangesCCD.length === 1,
        ALL_SOURCES !== sourceType,
      ),
      barPercentage: 0.92,
      ranges: rangesCCD,
      stack: ALL_SOURCES === sourceType ? 2 : 1,
      sourceType: { title: ccdSourceTooltipLabel, code: DATA_SOURCE_TYPE.CCD },
    },
    {
      label: 'valueDataCCD',
      data: [CCD, ALL_SOURCES].includes(sourceType) ? valueDataCCD : [],
      backgroundColor: '#f87f5c',
      hoverBackgroundColor: '#f87f5c',
      hoverBorderWidth: 1,
      barPercentage: 0.92,
      categoryPercentage: getCategoryPercentageWithFilter(
        activeFilter,
        singleDayMode,
        rangesCCD.length === 1,
        ALL_SOURCES !== sourceType,
      ),
      barThickness: 'flex',
      ranges: rangesCCD,
      stack: ALL_SOURCES === sourceType ? 2 : 1,
      sourceType: { title: ccdSourceTooltipLabel, code: DATA_SOURCE_TYPE.CCD },
    },
  ];
  const saturationData = {
    ...rowData,
    timeSets: { ccd: timeSetsCCD, devices: timeSetsDevices },
    datasets,
    averageOrTotal: {
      ...rowData.averageOrTotal,
      value:
        checkForAvailableSourceData(safeSaturationDataCCD) ||
        checkForAvailableSourceData(safeSaturationDataDevices)
          ? safeRangeTitle
          : '',
    },
    availableDataSource: {
      ccd: checkForAvailableSourceData(safeSaturationDataCCD),
      devices: checkForAvailableSourceData(safeSaturationDataDevices),
    },
    optionsToolTip: createTooltips(datasets),
  };

  const saturationOptions = merge(
    { ...options },
    {
      tooltips: {
        filter: tooltips => [1, 3].includes(tooltips.datasetIndex),
      },
      scales: {
        yAxes: [
          {
            ticks: {
              min: concatMinLimitForYAxis < 0 ? 0 : concatMinLimitForYAxis,
              max:
                concatMaxLimitForYAxis > 100 && !isWithoutLimit
                  ? 100
                  : concatMaxLimitForYAxis,
            },
          },
        ],
      },
    },
  );

  return { saturationData, saturationOptions };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Chart/useSaturationDataGraf.jsx